import React, {
  Key,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import { navigate } from 'gatsby'
import clsx from 'clsx'
import moment from 'moment'
import Layout from 'components/layouts/tow-column'
import { bookingsErrorHandling } from 'lib/hooks'
import {
  Alert,
  Button,
  Card,
  ErrorAlert,
  Form,
  Icon,
  SEO,
  showMessage,
  Title,
} from 'components/common'
import {
  BookingDatetimeSelect,
  BookingDetail,
  BookingInfoCard,
  BookingUsageScheduleDetail,
  CustomerSearchCard,
  FilterFormElement,
  PriceDisplayCard,
  PriceList,
  SelectedVariant,
  TimezoneSelectorModalCascader,
  VariantSelectorModalForm,
} from 'components/bookings'
import {
  Customer,
  CustomerConditionType,
  CustomerQueryInput,
  CustomerQueryType,
  OrderCreateInput,
  OrderVariantInput,
  Representative,
  useCustomersLazyQuery,
  useOrderCreateMutation,
  usePriceCalculateMutation,
  useRatesQuery,
  useGeneralSettingQuery,
  OrderPurpose,
} from 'lib/graphql/storeadmin'
import { routes } from 'config'
import { Anchor } from '../status'
import * as styles from './styles.module.css'
import ProductSelectCard from 'components/bookings/cards/ProductSelectCard'
import SagaArenaProductSelectCard from 'components/bookings/cards/SagaArenaProductSelectCard'

export const Head = () => <SEO title="予約の作成" />

type AlertMessage = {
  datetime?: string
  products?: string
  customer?: string
  bookingDetail?: string
}

const onBack = () => navigate(routes.bookings)

const icon = (
  <div className={clsx(styles.status__icon)}>
    <Icon.InfoCircleTwoTone twoToneColor="#52c41a" />
  </div>
)
export interface CustomerRepresentative extends Representative {
  customerType?: string
  corporationName?: string
}

const NewPage = (): JSX.Element => {
  const { data: settingsData } = useGeneralSettingQuery()
  const [OrderCreate, { loading }] = useOrderCreateMutation()
  const [form] = Form.useForm<VariantSelectorModalForm>()
  const [bookingDates, setBookingDates] =
    useState<[moment.Moment, moment.Moment]>(null)
  const [timezone, setTimezone] = useState<[moment.Moment, moment.Moment]>(null)
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([])
  const [customerInfo, setCustomerInfo] = useState<Customer>(null)
  const [alert, setAlert] = useState<AlertMessage>({})
  const [bookingDetail, setBookingDetail] = useState<BookingDetail>(null)
  const [bookingUsageScheduleDetail, setBookingUsageScheduleDetail] =
    useState<BookingUsageScheduleDetail>({})
  const [rateVariantId, setRateVariantId] = useState<string>(null)
  const rateId = useRef<string>(null)
  const [getPrices, { loading: priceLoading }] = usePriceCalculateMutation({})
  const [priceList, setPriceList] = useState<PriceList>({
    originalSubTotalPrice: 0,
    originalSubTotalPriceTaxExcluded: 0,
    originalTotalTax: 0,
    originalTotalPrice: 0,
    subTotal: 0,
    subTotalPriceTaxExcluded: 0,
    totalTax: 0,
    totalPrice: 0,
    discountPrice: 0,
    specialPriceIncluded: false,
  })
  const [checkManualPeriodTime, setManualPeriodTime] = useState<boolean>(false)

  // Order Create error useState
  const [showOrderCreateErrorAlert, setShowOrderCreateErrorAlert] =
    useState(false)
  const [orderCreateErrorDescriptions, setOrderCreateErrorDescriptions] =
    useState<string[]>([])
  const [selectedVariants, setSelectedVariants] =
    useState<SelectedVariant[]>(null)
  const [sagaArenaSelectedVariants, setSagaArenaSelectedVariants] = useState<
    OrderVariantInput[]
  >([])
  const [sagaArenaPurpose, setSagaArenaPurpose] = useState<OrderPurpose>(null)
  const [sagaArenaEstimateNumber, setSagaArenaEstimateNumber] = useState({
    first: '',
    second: '',
  })

  // SAGAアリーナかどうかをチェック
  const isSagaArena = useMemo(() => {
    return settingsData?.generalSetting.domain === 'saga-arena'
  }, [settingsData])

  const handleDiffDates = useCallback((): Array<string> => {
    const startDate = bookingDates[0].clone()
    const dates: string[] = [startDate.format('YYYY-MM-DD')]
    while (bookingDates[1].diff(startDate, 'days') > 0) {
      startDate.add(1, 'days')
      dates.push(startDate.format('YYYY-MM-DD'))
    }
    return dates
  }, [bookingDates])

  const selectButton = useMemo<boolean>(() => {
    return !(bookingDates && timezone)
  }, [bookingDates, timezone])

  const inactive = useMemo<boolean>(() => {
    return selectedVariants?.length > 0 || sagaArenaSelectedVariants?.length > 0
  }, [sagaArenaSelectedVariants, selectedVariants])

  const handleAlert = (): AlertMessage => {
    const messages: AlertMessage = {}
    if (isSagaArena) {
      if (!sagaArenaSelectedVariants?.length) {
        messages.products = '予約対象を選択してください'
      }
      if (!sagaArenaPurpose) {
        messages.products = '利用目的を選択してください'
      }
    } else {
      if (!selectedVariants?.length) {
        messages.products = '予約対象を選択してください'
      }
    }
    if (!bookingDates || !timezone)
      messages.datetime = '利用予定日と時間帯の入力は必須です'
    if (!customerInfo) messages.customer = 'お客様の選択は必須です'
    if (!bookingDetail)
      messages.bookingDetail = '予約詳細情報を入力してください'
    return messages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  const {
    data: ratesData,
    loading: ratesLoading,
    error: ratesError,
  } = useRatesQuery({})

  const dataSource = useMemo<Array<TimezoneSelectorModalCascader>>(() => {
    return ratesData?.rates?.edges?.map(({ rate }) => {
      const { id, title, variants } = rate
      return {
        key: id,
        title: title.jaJp,
        variants: variants?.edges?.map(({ variant }) => ({
          start: variant.period.start,
          end: variant.period.end,
          id: id,
          variantId: variant.id,
          category: title.jaJp,
        })),
      }
    })
  }, [ratesData])

  // ここから CustomerSearchCard 用関数
  const [searchCustomers, { data: customersData, loading: customersLoading }] =
    useCustomersLazyQuery()

  const handleSearchCustomer = (values: FilterFormElement) => {
    const inputs: CustomerQueryInput[] = []
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        inputs.push({
          field: key,
          value: value?.replace(/\s{2,}/g, ' ').trim(),
          condition: CustomerConditionType.Contains,
          type: CustomerQueryType.And,
        })
      }
    })
    searchCustomers({ variables: { input: inputs } })
  }

  const onSelectCustomer = (value: Customer) => {
    setCustomerInfo(value || null)
  }
  // ここまで CustomerSearchCard 用関数

  const onCheck = (checkedKeysValue: Key[]) => {
    setCheckedKeys(checkedKeysValue)
  }

  const onCloseAlert = () => {
    setShowOrderCreateErrorAlert(false)
  }

  /**
   * 予約作成用オブジェクトを作成する
   */
  const generateCreateOrderInput = (
    inputVariants: OrderVariantInput[]
  ): OrderCreateInput => {
    const { first, second } = sagaArenaEstimateNumber
    const contactOfRepresentativeName =
      bookingDetail.contactOfRepresentative?.name ??
      customerInfo.lastName + customerInfo.firstName
    const contactOfRepresentativeEmail =
      bookingDetail.contactOfRepresentative?.email ?? customerInfo.email
    const contactOfRepresentativePhone =
      bookingDetail.contactOfRepresentative?.phone ?? customerInfo.phone
    return {
      bookingDate: {
        start: bookingDates[0].format('YYYY-MM-DD'),
        end: bookingDates[1].format('YYYY-MM-DD'),
      },
      period:
        checkManualPeriodTime && rateVariantId === null
          ? {
              start: timezone[0].format('HH:mm'),
              end: timezone[1].format('HH:mm'),
            }
          : null,
      productVariants: inputVariants,
      estimateNumber:
        isSagaArena && first?.length > 0 && second?.length > 0
          ? first + '-' + second
          : null,
      rateVariantId:
        !checkManualPeriodTime && rateVariantId !== null ? rateVariantId : null,
      numberOfPeople: bookingDetail?.numberOfPeople,
      contactOfRepresentative: {
        name: contactOfRepresentativeName,
        email: contactOfRepresentativeEmail,
        phone: contactOfRepresentativePhone,
      },
      customerId: customerInfo.id,
      purpose: isSagaArena ? sagaArenaPurpose : bookingDetail?.purpose,
      entryFee: bookingDetail?.entryFee,
      saleOfGoods: bookingDetail?.saleOfGoods,
      calendar: {
        approval: bookingUsageScheduleDetail?.approval,
        organizer: bookingUsageScheduleDetail?.organizer,
        eventName: bookingUsageScheduleDetail?.eventName,
        publishableDate: bookingUsageScheduleDetail?.publishableDate,
      },
    }
  }

  /**
   * 予約を作成する
   */
  const onCreate = async (): Promise<void> => {
    const messages = handleAlert()
    setAlert(messages)
    if (Object.keys(messages).length) return

    try {
      // 予約作成用にバリエーションリストのひな型を定義
      let inputVariants: OrderVariantInput[] = []

      // SAGAアリーナとそれ以外のストアでバリエーションのデータ構造が異なるため、
      // 処理を分岐してinputVariantsにデータを格納する
      if (isSagaArena) {
        inputVariants = sagaArenaSelectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            arenaBookingInfo: variant.arenaBookingInfo,
          }
        })
      } else {
        inputVariants = selectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            quantity: 1,
          }
        })
      }

      // クエリを実行
      const { data } = await OrderCreate({
        // 予約作成用に整形したオブジェクトを渡す
        variables: { input: generateCreateOrderInput(inputVariants) },
      })

      // 実行結果に応じて処理を分岐
      const orderCreateErrors = data?.orderCreate?.orderUserErrors
      // エラー
      if (orderCreateErrors.length > 0) {
        const bulkErrorDescriptions = bookingsErrorHandling(orderCreateErrors)
        if (
          bulkErrorDescriptions[0] === 'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、予約を作成しました。'
          )
          await navigate(`${routes.bookings}/${data.orderCreate.order.id}`)
        } else {
          setShowOrderCreateErrorAlert(true)
          showMessage('error', '予約作成時にエラーが発生しました')
          setOrderCreateErrorDescriptions(bulkErrorDescriptions)
        }
        setOrderCreateErrorDescriptions(bulkErrorDescriptions)
      }
      // 成功
      else {
        setShowOrderCreateErrorAlert(false)
        showMessage('success', '予約を作成しました。')
        await navigate(`${routes.bookings}/${data.orderCreate.order.id}`)
      }
    } catch (e) {
      setShowOrderCreateErrorAlert(true)
      showMessage(
        'error',
        <>
          <div>予約作成時にエラーが発生しました</div>
          <div>{e.message}</div>
        </>
      )
    }
  }

  /**
   * 料金を計算して、料金リストのstateを更新する
   */
  const calculatePrices = async (inputVariants: OrderVariantInput[]) => {
    const isVariant = inputVariants?.every(({ variantId }) => variantId)
    if (!isVariant) return
    try {
      const response = await getPrices({
        variables: {
          input: {
            customerId: customerInfo?.id,
            productVariants: inputVariants,
            period: {
              start: timezone[0].format('HH:mm'),
              end: timezone[1].format('HH:mm'),
            },
            bookingDates: {
              start: bookingDates[0].format('YYYY-MM-DD'),
              end: bookingDates[bookingDates?.length - 1].format('YYYY-MM-DD'),
            },
          },
        },
      })

      const {
        originalSubTotalPrice,
        originalSubTotalPriceTaxExcluded,
        originalTotalTax,
        originalTotalPrice,
        subTotalPrice,
        subTotalPriceTaxExcluded,
        totalTax,
        totalPrice,
        specialPriceIncluded,
        discountPrice,
      } = response.data?.priceCalculate?.price || {}

      setPriceList((prevPriceList) => ({
        ...prevPriceList,
        originalSubTotalPrice: originalSubTotalPrice,
        originalSubTotalPriceTaxExcluded: originalSubTotalPriceTaxExcluded,
        originalTotalTax: originalTotalTax,
        originalTotalPrice: originalTotalPrice,
        subTotal: subTotalPrice,
        subTotalPriceTaxExcluded: subTotalPriceTaxExcluded,
        totalTax: totalTax,
        totalPrice: totalPrice,
        specialPriceIncluded: specialPriceIncluded,
        discountPrice: discountPrice,
      }))
    } catch (e) {
      console.log(e)
    }
  }

  // 料金計算処理を実行
  useEffect(() => {
    if (priceLoading || !customerInfo) return
    if (
      customerInfo &&
      bookingDates &&
      timezone &&
      (selectedVariants?.length > 0 || sagaArenaSelectedVariants?.length > 0)
    ) {
      let inputVariants: OrderVariantInput[] = []
      if (isSagaArena) {
        inputVariants = sagaArenaSelectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            arenaBookingInfo: variant.arenaBookingInfo,
          }
        })
      } else {
        inputVariants = selectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            quantity: 1,
          }
        })
      }
      // 料金を計算し、料金リストのstateを更新する
      calculatePrices(inputVariants)
    } else {
      setPriceList({
        originalSubTotalPrice: 0,
        originalSubTotalPriceTaxExcluded: 0,
        originalTotalTax: 0,
        originalTotalPrice: 0,
        subTotal: 0,
        subTotalPriceTaxExcluded: 0,
        totalTax: 0,
        totalPrice: 0,
        specialPriceIncluded: false,
        discountPrice: 0,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingDates,
    bookingDetail,
    customerInfo,
    selectedVariants,
    sagaArenaSelectedVariants,
    timezone,
  ])

  const clearProductData = () => {
    setCheckedKeys([])
    form.resetFields()
    setManualPeriodTime(false)
    if (isSagaArena) {
      setSagaArenaSelectedVariants([])
    }
  }

  return (
    <>
      <Layout title="予約を作成する" actions={<Anchor />} onBack={onBack}>
        <Layout.Section size="large">
          <Layout.Item>
            {showOrderCreateErrorAlert && (
              <ErrorAlert
                alertDescription={orderCreateErrorDescriptions}
                onCloseAlert={onCloseAlert}
              />
            )}
            <Card shadow>
              <div className={clsx('flex', 'justify-between', 'items-center')}>
                <div className={clsx('flex-initial')}>
                  <Title level={5}>予約日時</Title>
                </div>

                {(selectedVariants?.length > 0 ||
                  sagaArenaSelectedVariants?.length > 0) && (
                  <div className={clsx('flex', 'flex-auto', 'justify-end')}>
                    <Button
                      style={{
                        marginLeft: '3px',
                      }}
                      onClick={clearProductData}
                    >
                      予約日時を変更
                    </Button>
                  </div>
                )}
              </div>

              <div className={clsx('flex', 'justify-between', 'items-start')}>
                <BookingDatetimeSelect
                  timezone={timezone}
                  dataSource={dataSource}
                  onDateChange={setBookingDates}
                  onTimezoneChange={setTimezone}
                  loading={ratesLoading}
                  error={ratesError}
                  rateId={rateId}
                  inactive={inactive}
                  checkManualPeriodTime={checkManualPeriodTime}
                  setManualPeriodTime={setManualPeriodTime}
                  setRateVariantId={setRateVariantId}
                />
              </div>
            </Card>
            {alert?.datetime && (
              <Alert
                className="my-2"
                showIcon
                message={alert.datetime}
                type="error"
              />
            )}
          </Layout.Item>

          <Layout.Item>
            {isSagaArena ? (
              <SagaArenaProductSelectCard
                bookingDates={bookingDates}
                checkedKeys={checkedKeys}
                calculatePrices={calculatePrices}
                hasCustomerId={!!customerInfo?.id}
                disabled={selectButton}
                form={form}
                handleDiffDates={handleDiffDates}
                isSagaArena={isSagaArena}
                onCheck={onCheck}
                purpose={sagaArenaPurpose}
                priceLoading={priceLoading}
                rateId={rateId}
                setCheckedKeys={setCheckedKeys}
                setPurpose={setSagaArenaPurpose}
                selectedVariants={selectedVariants}
                setSelectedVariants={setSelectedVariants}
                setSagaArenaSelectedVariants={setSagaArenaSelectedVariants}
                sagaArenaSelectedVariants={sagaArenaSelectedVariants}
                sagaArenaEstimateNumber={sagaArenaEstimateNumber}
                setSagaArenaEstimateNumber={setSagaArenaEstimateNumber}
                specialPriceIncluded={priceList.specialPriceIncluded}
                timezone={timezone}
              />
            ) : (
              <Card shadow>
                <ProductSelectCard
                  hasCustomerId={!!customerInfo?.id}
                  bookingDates={bookingDates}
                  checkedKeys={checkedKeys}
                  calculatePrices={calculatePrices}
                  disabled={selectButton}
                  form={form}
                  handleDiffDates={handleDiffDates}
                  onCheck={onCheck}
                  rateId={rateId}
                  selectedVariants={selectedVariants}
                  setSelectedVariants={setSelectedVariants}
                  timezone={timezone}
                  isSagaArena={isSagaArena}
                />
              </Card>
            )}
            {alert?.products && (
              <Alert showIcon message={alert.products} type="error" />
            )}
          </Layout.Item>

          <Layout.Item>
            <PriceDisplayCard
              extensionData={[
                {
                  id: null,
                  value: 0,
                  reason: null,
                },
              ]}
              priceList={priceList}
              title={icon}
              subTitle={
                <div className={clsx(styles.status__text)}>
                  決済予定金額
                  {(selectedVariants?.length > 0 ||
                    sagaArenaSelectedVariants?.length > 0) &&
                    !customerInfo?.id && (
                      <span className="opacity-80 ml-4">
                        ※お客様を選択してください
                      </span>
                    )}
                </div>
              }
              loading={priceLoading}
            />
          </Layout.Item>
          {Object.keys(alert).length > 0 && (
            <Alert showIcon message="入力値が不足しています" type="error" />
          )}
        </Layout.Section>

        <Layout.Section size="small">
          <Layout.Item>
            <CustomerSearchCard
              title={'お客様を探す'}
              data={customersData}
              loading={customersLoading}
              onSearch={handleSearchCustomer}
              onSelect={onSelectCustomer}
            />
            {alert?.customer && (
              <Alert showIcon message={alert.customer} type="error" />
            )}
          </Layout.Item>

          <Layout.Item>
            <BookingInfoCard
              customer={{
                id: customerInfo?.id,
                firstName: customerInfo?.firstName,
                lastName: customerInfo?.lastName,
                customerType: customerInfo?.type,
                email: customerInfo?.email,
                phone: customerInfo?.phone,
                corporationName: customerInfo?.corporationName,
              }}
              bookingDetail={bookingDetail}
              onDetailHold={setBookingDetail}
              usageScheduleDetails={{
                approval: bookingUsageScheduleDetail.approval,
                organizer: bookingUsageScheduleDetail.organizer,
                eventName: bookingUsageScheduleDetail.eventName,
                publishableDate: bookingUsageScheduleDetail.publishableDate,
              }}
              onUsageScheduleDetailsHold={setBookingUsageScheduleDetail}
              editable={true}
              isArena={isSagaArena}
            />
            {alert?.bookingDetail && (
              <Alert showIcon message={alert.bookingDetail} type="error" />
            )}
          </Layout.Item>
        </Layout.Section>

        <Layout.Footer
          left={
            <Button danger onClick={onBack}>
              キャンセル
            </Button>
          }
          right={
            <div className={clsx('flex', 'justify-end')}>
              <Button type="primary" loading={loading} onClick={onCreate}>
                保存する
              </Button>
            </div>
          }
        />
      </Layout>
    </>
  )
}

export default NewPage
